import { Component } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Trans, withTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import withStyles from '@material-ui/core/styles/withStyles';
import Typography from '@material-ui/core/Typography';
import Modal from '@material-ui/core/Modal';
import Button from '@material-ui/core/Button';
import Check from '@material-ui/icons/Check';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import FormLabel from '@material-ui/core/FormLabel';
import { TableBody } from '@material-ui/core';
import { combineStyles, isGranted } from 'helpers/helpers';
import { get, post, put } from 'helpers/apiHelpers';
import { ROLE_EDIT_MONEY_BOX_CONFIGURATION } from 'helpers/roles';
import extendedFormsStyle from 'assets/jss/material-dashboard-pro-react/views/extendedFormsStyle';
import buttonsStyle from 'assets/jss/material-dashboard-pro-react/views/buttonsStyle';
import validationFormsStyle from 'assets/jss/material-dashboard-pro-react/views/validationFormsStyle';
import {
  successColor,
  whiteColor,
} from 'assets/jss/material-dashboard-pro-react';
import CardWrapper from 'components/Card/CardWrapper';
import CardBody from 'components/Card/CardBody';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import CustomInput from 'components/CustomInput/CustomInput';
import SelectInput from 'components/FormSelect/SelectInput';
import FormTextInput from 'components/FormTextInput/FormTextInput';
import FormImageUpload from 'components/FormImageUpload/FormImageUpload';
import LogView from 'components/History/LogView';
import { RadioGroup } from 'components/Form/Inputs';

const DiscountElementTypes = t => [
  {
    name: t('common.shared.percentage'),
    value: true,
  },
  {
    name: t('common.shared.amount'),
    value: false,
  },
];

const recommendationModeOptions = (t, currency) => [
  {
    name: t('common.moneyBox.forTheCommand'),
    value: 'RECOMMENDATION_MODE_LINEAR',
  },
  {
    name: t('common.moneyBox.forEachCoin', { currency }),
    value: 'RECOMMENDATION_MODE_PROGRESSIVE',
  },
];

const pointsValidityPeriodOptions = t => [
  {
    value: 'NONE',
    label: t('common.moneyboxconfig.detailSettings.validityPeriod.none'),
  },
  {
    value: 'MONTHS_3',
    label: t('common.moneyboxconfig.detailSettings.validityPeriod.months3'),
  },
  {
    value: 'MONTHS_6',
    label: t('common.moneyboxconfig.detailSettings.validityPeriod.months6'),
  },
  {
    value: 'MONTHS_12',
    label: t('common.moneyboxconfig.detailSettings.validityPeriod.months12'),
  },
];

const styles = {
  customInputWrapper: {
    marginTop: '15px',
  },
  helpTestWrapper: {
    width: '120px',
  },
  selectInputWrapper: {
    marginRight: '-15px',
    marginTop: '-7px',
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalContentWrapper: {
    background: whiteColor,
    width: '25%',
    padding: '30px',
  },
  typography: { marginBottom: '50px' },
  modalButtonWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '40px',
  },
  buttonYes: {
    paddingInline: '35px',
  },
  buttonNo: {
    background: successColor[0],
    paddingInline: '35px',
  },
};

class MoneyBoxConfigurations extends Component {
  state = {
    shortName: '',
    singularNameDenominator: '',
    pluralNameDenominator: '',
    singularNameGenitive: '',
    pluralNameGenitive: '',
    singularNameLocative: '',
    pluralNameLocative: '',
    icon: null,
    iconUrl: null,
    config: {
      forCash: '',
      cheaperDietDifference: '',
      pointsValue: '',
      forPoints: '',
      cashValue: '',
      markPointsValue: '',
      isDiscountPercentage: true,
      discountValue: '',
      pointsForRecommendation: '',
      pointsForRegister: '',
      allowPayWithMoneyboxInSubscriptionMode: false,
      recommendationMode: 'RECOMMENDATION_MODE_LINEAR',
      pointsValidityPeriod: 'NONE',
      smsBonus: '',
      emailBonus: '',
      phoneBonus: '',
    },
    violations: { key: '', message: '' },
    isEdit: false,
    isLoading: true,
    isMounting: true,
    modalIsOpen: false,
    moneyBoxReadMoreLink: this.props.moneyBoxReadMoreLink || '',
  };

  // eslint-disable-next-line require-await -- FIXME
  async componentDidMount() {
    // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
    get('/money-box-configurations', { pagination: false }).then(res => {
      const foundConfiguration = res['hydra:member'].length > 0;
      this.setState(prevState => ({
        shortName: foundConfiguration
          ? res['hydra:member'][0].names.shortName
          : prevState.shortName,
        singularNameDenominator: foundConfiguration
          ? res['hydra:member'][0].names.singularNameDenominator
          : prevState.singularNameDenominator,
        pluralNameDenominator: foundConfiguration
          ? res['hydra:member'][0].names.pluralNameDenominator
          : prevState.pluralNameDenominator,
        singularNameGenitive: foundConfiguration
          ? res['hydra:member'][0].names.singularNameGenitive
          : prevState.singularNameGenitive,
        pluralNameGenitive: foundConfiguration
          ? res['hydra:member'][0].names.pluralNameGenitive
          : prevState.pluralNameGenitive,
        singularNameLocative: foundConfiguration
          ? res['hydra:member'][0].names.singularNameLocative
          : prevState.singularNameLocative,
        pluralNameLocative: foundConfiguration
          ? res['hydra:member'][0].names.pluralNameLocative
          : prevState.pluralNameLocative,
        icon: foundConfiguration
          ? res['hydra:member'][0].icon?.['@id']
          : prevState.icon,
        iconUrl: foundConfiguration
          ? res['hydra:member'][0].icon?.contentUrl
          : prevState.iconUrl,
        config: foundConfiguration
          ? { ...prevState.config, ...res['hydra:member'][0] }
          : prevState.config,
        isEdit: foundConfiguration ? true : prevState.isEdit,
        isLoading: false,
        isMounting: false,
      }));
    });
  }

  getImage = (stateName, data) => {
    this.setState({
      [stateName]: data?.['@id'] || null,
      [`${stateName}Url`]: data?.contentUrl || null,
    });
  };

  removeImage = stateName => {
    this.setState({
      [stateName]: null,
      [`${stateName}Url`]: null,
    });
  };

  handleNameChange = (name, value) => {
    this.setState({
      [name]: value,
    });
  };

  // eslint-disable-next-line consistent-return -- FIXME
  handleChange = (name, value) => {
    if (isNaN(value)) {
      return false;
    }
    this.setState({
      config: {
        ...this.state.config,
        [name]: value,
      },
    });
  };

  handleModal = () => {
    this.setState({
      modalIsOpen: !this.state.modalIsOpen,
    });
  };

  handleMoneyBoxLinkChange = (value, name) => {
    this.setState({
      [name]: value,
    });
  };

  handleSubmit = () => {
    this.handleModal();
    const { config } = this.state;

    const pointsData = {
      shortName: this.state.shortName,
      singularNameDenominator: this.state.singularNameDenominator,
      pluralNameDenominator: this.state.pluralNameDenominator,
      singularNameGenitive: this.state.singularNameGenitive,
      pluralNameGenitive: this.state.pluralNameGenitive,
      singularNameLocative: this.state.singularNameLocative,
      pluralNameLocative: this.state.pluralNameLocative,
    };

    if (
      Object.values(pointsData).includes('') &&
      Object.values(pointsData).find(value => value !== '')
    ) {
      return toast.error(this.props.t('common.moneyboxconfig.fillAllFields'));
    }

    const marketingConsentFields = {
      smsBonus: parseFloat(config.smsBonus ?? 0),
      emailBonus: parseFloat(config.emailBonus ?? 0),
      phoneBonus: parseFloat(config.phoneBonus ?? 0),
    };

    const getMarketingFieldsToSend = () => marketingConsentFields;

    const data = {
      names: pointsData,
      forCash: parseFloat(config.forCash),

      pointsValue: parseFloat(config.pointsValue),
      forPoints: parseFloat(config.forPoints),
      cashValue: parseFloat(config.cashValue),
      markPointsValue: parseFloat(config.markPointsValue),
      isDiscountPercentage: config.isDiscountPercentage,
      discountValue: parseFloat(config.discountValue),
      pointsForRecommendation: parseFloat(config.pointsForRecommendation),
      pointsForRegister: parseFloat(config.pointsForRegister),
      cheaperDietDifference: parseFloat(config.cheaperDietDifference),

      recommendationMode: config.recommendationMode,
      pointsValidityPeriod: config.pointsValidityPeriod,
      icon: this.state.icon,
      allowPayWithMoneyboxInSubscriptionMode:
        config.allowPayWithMoneyboxInSubscriptionMode,
      ...getMarketingFieldsToSend(),
    };

    put(`/brands/${this.props.brandId}`, {
      moneyBoxReadMoreLink: this.state.moneyBoxReadMoreLink,
    });

    return this.state.isEdit
      ? // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
        put(this.state.config['@id'], data).then(
          () => toast.success(this.props.t('common.shared.savedChanges')),
          () =>
            toast.error(
              this.props.t('common.moneyboxconfig.formFilledIncorrect')
            )
        )
      : // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
        post('money-box-configurations', data).then(() =>
          // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
          get('/money-box-configurations').then(
            res => {
              toast.success(this.props.t('common.shared.savedChanges'));
              this.setState(prevState => ({
                config:
                  res['hydra:member'].length > 0
                    ? { ...prevState.config, ...res['hydra:member'][0] }
                    : prevState.config,
                isEdit:
                  res['hydra:member'].length > 0 ? true : prevState.isEdit,
                isLoading: false,
              }));
            },
            () =>
              toast.error(
                this.props.t('common.moneyboxconfig.formFilledIncorrect')
              )
          )
        );
  };

  render() {
    const {
      t,
      classes,
      multinational: { currency },
    } = this.props;

    return (
      <form
        style={{
          opacity: this.state.isMounting ? 0 : 1,
          transition: '0.3s all',
        }}
      >
        <CardWrapper title={t('common.moneyboxconfig.points')}>
          <CardBody>
            <GridItem md={12}>
              <GridContainer>
                <GridItem md={6}>
                  <p>{t('common.moneyboxconfig.pointsInfo')}</p>
                </GridItem>
              </GridContainer>
              <GridContainer>
                <GridItem md={3}>
                  <FormLabel className={classes.labelHorizontal}>
                    {t('common.moneyboxconfig.shortName')}:
                  </FormLabel>
                  <FormTextInput
                    classes={classes}
                    value={this.state.shortName}
                    name="shortName"
                    placeholder={t(
                      'common.moneyboxconfig.shortNamePlaceholder'
                    )}
                    handleChange={e =>
                      this.handleNameChange(e.target.name, e.target.value)
                    }
                  />
                </GridItem>
              </GridContainer>
              <GridContainer>
                <GridItem md={3}>
                  <FormLabel className={classes.labelHorizontal}>
                    {t('common.moneyboxconfig.fullname1')}:
                  </FormLabel>
                  <FormTextInput
                    classes={classes}
                    value={this.state.singularNameDenominator}
                    name="singularNameDenominator"
                    placeholder={t(
                      'common.moneyboxconfig.fullname1Placeholder'
                    )}
                    handleChange={e =>
                      this.handleNameChange(e.target.name, e.target.value)
                    }
                  />
                </GridItem>
                <GridItem md={3}>
                  <FormLabel className={classes.labelHorizontal}>
                    {t('common.moneyboxconfig.fullname2')}:
                  </FormLabel>
                  <FormTextInput
                    classes={classes}
                    value={this.state.pluralNameDenominator}
                    name="pluralNameDenominator"
                    placeholder={t(
                      'common.moneyboxconfig.fullname2Placeholder'
                    )}
                    handleChange={e =>
                      this.handleNameChange(e.target.name, e.target.value)
                    }
                  />
                </GridItem>
              </GridContainer>
              <GridContainer>
                <GridItem md={3}>
                  <FormLabel className={classes.labelHorizontal}>
                    {t('common.moneyboxconfig.fullname3')}:
                  </FormLabel>
                  <FormTextInput
                    classes={classes}
                    value={this.state.singularNameGenitive}
                    name="singularNameGenitive"
                    placeholder={t(
                      'common.moneyboxconfig.fullname3Placeholder'
                    )}
                    handleChange={e =>
                      this.handleNameChange(e.target.name, e.target.value)
                    }
                  />
                </GridItem>
                <GridItem md={3}>
                  <FormLabel className={classes.labelHorizontal}>
                    {t('common.moneyboxconfig.fullname4')}:
                  </FormLabel>
                  <FormTextInput
                    classes={classes}
                    value={this.state.pluralNameGenitive}
                    name="pluralNameGenitive"
                    placeholder={t(
                      'common.moneyboxconfig.fullname4Placeholder'
                    )}
                    handleChange={e =>
                      this.handleNameChange(e.target.name, e.target.value)
                    }
                  />
                </GridItem>
              </GridContainer>
              <GridContainer>
                <GridItem md={3}>
                  <FormLabel className={classes.labelHorizontal}>
                    {t('common.moneyboxconfig.fullname5')}:
                  </FormLabel>
                  <FormTextInput
                    classes={classes}
                    value={this.state.singularNameLocative}
                    name="singularNameLocative"
                    placeholder={t(
                      'common.moneyboxconfig.fullname5Placeholder'
                    )}
                    handleChange={e =>
                      this.handleNameChange(e.target.name, e.target.value)
                    }
                  />
                </GridItem>
                <GridItem md={3}>
                  <FormLabel className={classes.labelHorizontal}>
                    {t('common.moneyboxconfig.fullname6')}:
                  </FormLabel>
                  <FormTextInput
                    classes={classes}
                    value={this.state.pluralNameLocative}
                    name="pluralNameLocative"
                    placeholder={t(
                      'common.moneyboxconfig.fullname6Placeholder'
                    )}
                    handleChange={e =>
                      this.handleNameChange(e.target.name, e.target.value)
                    }
                  />
                </GridItem>
              </GridContainer>
              <GridContainer>
                <GridItem md={3}>
                  <FormImageUpload
                    label={t('common.moneyboxconfig.icon')}
                    stateName="icon"
                    getImage={this.getImage}
                    removeImage={this.removeImage}
                    previewUrl={this.state.iconUrl}
                  />
                </GridItem>
              </GridContainer>
            </GridItem>
          </CardBody>
        </CardWrapper>
        <CardWrapper title={t('common.moneyboxconfig.config')}>
          <CardBody>
            <GridItem>
              <GridContainer>
                <GridItem md={12}>
                  <Table className={classes.table}>
                    <TableHead>
                      <TableRow>
                        <TableCell width="30%">
                          {t('common.moneyboxconfig.description')}
                        </TableCell>
                        <TableCell />
                        <TableCell>
                          {t('common.moneyboxconfig.amountSpent', {
                            currency,
                          })}
                        </TableCell>
                        <TableCell />
                        <TableCell>
                          {t('common.moneyboxconfig.pointsCount')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          {t('common.moneyboxconfig.desc1')}
                          <br />
                          <br />
                          {t('common.shared.etc')}{' '}
                          <i>
                            {t('common.moneyboxconfig.etc1', {
                              currency,
                            })}
                          </i>
                        </TableCell>
                        <TableCell align="right">
                          <b>{t('common.moneyboxconfig.spent')}</b>
                        </TableCell>
                        <TableCell>
                          <div style={styles.customInputWrapper}>
                            <CustomInput
                              formControlProps={{ fullWidth: true }}
                              error={this.state.config.forCash === ''}
                              helpText={
                                !this.state.isLoading &&
                                this.state.config.forCash === '' ? (
                                  <div style={styles.helpTestWrapper}>
                                    {t(
                                      'common.moneyboxconfig.valueCannotBeEmpty'
                                    )}
                                  </div>
                                ) : undefined
                              }
                              inputProps={{
                                disabled: !isGranted(
                                  ROLE_EDIT_MONEY_BOX_CONFIGURATION
                                ),
                                type: 'text',
                                placeholder: t(
                                  'common.moneyboxconfig.typeValue'
                                ),
                                name: 'forCash',
                                value: this.state.config.forCash,
                                onChange: e =>
                                  this.handleChange(
                                    e.target.name,
                                    e.target.value
                                  ),
                              }}
                            />
                          </div>
                        </TableCell>
                        <TableCell align="right">
                          <b>{t('common.moneyboxconfig.clientGetPoints')}:</b>
                        </TableCell>
                        <TableCell>
                          <div style={styles.customInputWrapper}>
                            <CustomInput
                              formControlProps={{ fullWidth: true }}
                              error={this.state.config.pointsValue === ''}
                              helpText={
                                !this.state.isLoading &&
                                this.state.config.pointsValue === '' ? (
                                  <div style={styles.helpTestWrapper}>
                                    {t(
                                      'common.moneyboxconfig.valueCannotBeEmpty'
                                    )}
                                  </div>
                                ) : undefined
                              }
                              inputProps={{
                                disabled: !isGranted(
                                  ROLE_EDIT_MONEY_BOX_CONFIGURATION
                                ),
                                type: 'text',
                                placeholder: t(
                                  'common.moneyboxconfig.typeValue'
                                ),
                                name: 'pointsValue',
                                value: this.state.config.pointsValue,
                                onChange: e =>
                                  this.handleChange(
                                    e.target.name,
                                    e.target.value
                                  ),
                              }}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell>
                          {t('common.moneyboxconfig.pointsCount')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          {t('common.moneyboxconfig.desc2')}
                          <br />
                          <br />
                          {t('common.shared.etc')}{' '}
                          <i>
                            {t('common.moneyboxconfig.etc2', {
                              currency,
                            })}
                          </i>
                        </TableCell>
                        <TableCell align="right">
                          <b>{t('common.moneyboxconfig.forAny1')}</b>
                        </TableCell>
                        <TableCell align="left">
                          <b>
                            {t('common.moneyboxconfig.diff', {
                              currency,
                            })}
                          </b>
                        </TableCell>
                        <TableCell align="right">
                          <b>{t('common.moneyboxconfig.clientGetPoints')}:</b>
                        </TableCell>
                        <TableCell>
                          <div style={styles.customInputWrapper}>
                            <CustomInput
                              formControlProps={{ fullWidth: true }}
                              error={
                                this.state.config.cheaperDietDifference === ''
                              }
                              helpText={
                                !this.state.isLoading &&
                                this.state.config.cheaperDietDifference ===
                                  '' ? (
                                  <div style={styles.helpTestWrapper}>
                                    {t(
                                      'common.moneyboxconfig.valueCannotBeEmpty'
                                    )}
                                  </div>
                                ) : undefined
                              }
                              inputProps={{
                                disabled: !isGranted(
                                  ROLE_EDIT_MONEY_BOX_CONFIGURATION
                                ),
                                type: 'text',
                                placeholder: t(
                                  'common.moneyboxconfig.typeValue'
                                ),
                                name: 'cheaperDietDifference',
                                value: this.state.config.cheaperDietDifference,
                                onChange: e =>
                                  this.handleChange(
                                    e.target.name,
                                    e.target.value
                                  ),
                              }}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        <TableCell />
                        <TableCell>
                          {t('common.moneyboxconfig.pointsCount')}
                        </TableCell>
                        <TableCell />
                        <TableCell>
                          {t('common.moneyboxconfig.discountAmount', {
                            currency,
                          })}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          {t('common.moneyboxconfig.desc3')}
                          <br />
                          <br />
                          {t('common.shared.etc')}{' '}
                          <i>
                            {t('common.moneyboxconfig.etc3', {
                              currency,
                            })}
                          </i>
                        </TableCell>
                        <TableCell align="right">
                          <b>{t('common.moneyboxconfig.forAnySpent')}</b>
                        </TableCell>
                        <TableCell>
                          <div style={styles.customInputWrapper}>
                            <CustomInput
                              formControlProps={{ fullWidth: true }}
                              error={this.state.config.forPoints === ''}
                              helpText={
                                !this.state.isLoading &&
                                this.state.config.forPoints === '' ? (
                                  <div style={styles.helpTestWrapper}>
                                    {t(
                                      'common.moneyboxconfig.valueCannotBeEmpty'
                                    )}
                                  </div>
                                ) : undefined
                              }
                              inputProps={{
                                disabled: !isGranted(
                                  ROLE_EDIT_MONEY_BOX_CONFIGURATION
                                ),
                                type: 'text',
                                placeholder: t(
                                  'common.moneyboxconfig.typeValue'
                                ),
                                name: 'forPoints',
                                value: this.state.config.forPoints,
                                onChange: e =>
                                  this.handleChange(
                                    e.target.name,
                                    e.target.value
                                  ),
                              }}
                            />
                          </div>
                        </TableCell>
                        <TableCell align="right">
                          <b>{t('common.moneyboxconfig.clientGetDiscount')}:</b>
                        </TableCell>
                        <TableCell>
                          <div style={styles.customInputWrapper}>
                            <CustomInput
                              formControlProps={{ fullWidth: true }}
                              error={this.state.config.cashValue === ''}
                              helpText={
                                !this.state.isLoading &&
                                this.state.config.cashValue === '' ? (
                                  <div style={styles.helpTestWrapper}>
                                    {t(
                                      'common.moneyboxconfig.valueCannotBeEmpty'
                                    )}
                                  </div>
                                ) : undefined
                              }
                              inputProps={{
                                disabled: !isGranted(
                                  ROLE_EDIT_MONEY_BOX_CONFIGURATION
                                ),
                                type: 'text',
                                placeholder: t(
                                  'common.moneyboxconfig.typeValue'
                                ),
                                name: 'cashValue',
                                value: this.state.config.cashValue,
                                onChange: e =>
                                  this.handleChange(
                                    e.target.name,
                                    e.target.value
                                  ),
                              }}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell>
                          {t('common.moneyboxconfig.pointsCount')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          {t('common.moneyboxconfig.desc4')}
                          <br />
                          <br />
                          {t('common.shared.etc')}{' '}
                          <i>{t('common.moneyboxconfig.etc4')}</i>
                        </TableCell>
                        <TableCell align="right">
                          <b>{t('common.moneyboxconfig.forAny2')}</b>
                        </TableCell>
                        <TableCell align="left">
                          <b>{t('common.moneyboxconfig.oneRate')}</b>
                        </TableCell>
                        <TableCell align="right">
                          <b>{t('common.moneyboxconfig.clientGetPoints')}:</b>
                        </TableCell>
                        <TableCell>
                          <div style={styles.customInputWrapper}>
                            <CustomInput
                              formControlProps={{ fullWidth: true }}
                              error={this.state.config.markPointsValue === ''}
                              helpText={
                                !this.state.isLoading &&
                                this.state.config.markPointsValue === '' ? (
                                  <div style={styles.helpTestWrapper}>
                                    {t(
                                      'common.moneyboxconfig.valueCannotBeEmpty'
                                    )}
                                  </div>
                                ) : undefined
                              }
                              inputProps={{
                                disabled: !isGranted(
                                  ROLE_EDIT_MONEY_BOX_CONFIGURATION
                                ),
                                type: 'text',
                                placeholder: t(
                                  'common.moneyboxconfig.typeValue'
                                ),
                                name: 'markPointsValue',
                                value: this.state.config.markPointsValue,
                                onChange: e =>
                                  this.handleChange(
                                    e.target.name,
                                    e.target.value
                                  ),
                              }}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell>
                          {t('common.moneyboxconfig.discountValue')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          {t('common.moneyboxconfig.desc5')}
                          <br />
                          <br />
                          {t('common.shared.etc')}{' '}
                          <i>
                            {t('common.moneyboxconfig.etc5', {
                              currency,
                            })}
                          </i>
                        </TableCell>
                        <TableCell align="right">
                          <b>{t('common.moneyboxconfig.forUseCode')}</b>
                        </TableCell>
                        <TableCell align="left">
                          <b>{t('common.moneyboxconfig.clientGetDiscount')}</b>
                        </TableCell>
                        <TableCell align="left">
                          <div style={styles.selectInputWrapper}>
                            <SelectInput
                              classes={this.props.classes}
                              trackBy="value"
                              mapBy="name"
                              disabled={
                                !isGranted(ROLE_EDIT_MONEY_BOX_CONFIGURATION)
                              }
                              // eslint-disable-next-line new-cap -- FIXME
                              options={DiscountElementTypes(t)}
                              value={this.state.config.isDiscountPercentage}
                              handleChange={(ev, obj) =>
                                this.setState(prevState => ({
                                  config: {
                                    ...prevState.config,
                                    isDiscountPercentage: obj.value,
                                  },
                                }))
                              }
                            />
                          </div>
                        </TableCell>
                        <TableCell>
                          <div style={styles.customInputWrapper}>
                            <CustomInput
                              formControlProps={{ fullWidth: true }}
                              error={this.state.config.discountValue === ''}
                              helpText={
                                !this.state.isLoading &&
                                this.state.config.discountValue === '' ? (
                                  <div style={styles.helpTestWrapper}>
                                    {t(
                                      'common.moneyboxconfig.valueCannotBeEmpty'
                                    )}
                                  </div>
                                ) : undefined
                              }
                              inputProps={{
                                disabled: !isGranted(
                                  ROLE_EDIT_MONEY_BOX_CONFIGURATION
                                ),
                                type: 'text',
                                placeholder: t(
                                  'common.moneyboxconfig.typeValue'
                                ),
                                name: 'discountValue',
                                value: this.state.config.discountValue,
                                onChange: e =>
                                  this.handleChange(
                                    e.target.name,
                                    e.target.value
                                  ),
                              }}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell>
                          {t('common.moneyboxconfig.pointsQty')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          {t('common.moneyboxconfig.desc6')}
                          <br />
                          <br />
                          <SelectInput
                            classes={this.props.classes}
                            trackBy="value"
                            mapBy="name"
                            label={t('prices.moneyBoxConf.modeOfAction')}
                            disabled={
                              !isGranted(ROLE_EDIT_MONEY_BOX_CONFIGURATION)
                            }
                            options={recommendationModeOptions(t, currency)}
                            value={this.state.config.recommendationMode}
                            handleChange={(ev, obj) =>
                              this.setState(prevState => ({
                                config: {
                                  ...prevState.config,
                                  recommendationMode: obj.value,
                                },
                              }))
                            }
                          />
                          {t('common.shared.etc')}
                          {this.state.config.recommendationMode ===
                            'RECOMMENDATION_MODE_LINEAR' && (
                            <i>{t('common.moneyboxconfig.etc6')}</i>
                          )}
                          {this.state.config.recommendationMode ===
                            'RECOMMENDATION_MODE_PROGRESSIVE' && (
                            <i>
                              {t('common.moneyboxconfig.etc7', {
                                currency,
                              })}
                            </i>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {this.state.config.recommendationMode ===
                            'RECOMMENDATION_MODE_LINEAR' && (
                            <b>{t('common.moneyboxconfig.forRefNewClient')}</b>
                          )}
                          {this.state.config.recommendationMode ===
                            'RECOMMENDATION_MODE_PROGRESSIVE' && (
                            <b>
                              {t('common.moneyBox.forEachCoin', {
                                currency,
                              })}
                            </b>
                          )}
                        </TableCell>
                        <TableCell align="left">
                          {this.state.config.recommendationMode ===
                            'RECOMMENDATION_MODE_LINEAR' && (
                            <b>{t('common.moneyboxconfig.witchUseCode')}</b>
                          )}
                          {this.state.config.recommendationMode ===
                            'RECOMMENDATION_MODE_PROGRESSIVE' && (
                            <b>
                              {t('common.moneyboxconfig.spentForNewClient')}
                            </b>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <b>{t('common.moneyboxconfig.refGetPoints')}:</b>
                        </TableCell>
                        <TableCell>
                          <div style={styles.customInputWrapper}>
                            <CustomInput
                              formControlProps={{ fullWidth: true }}
                              error={
                                this.state.config.pointsForRecommendation === ''
                              }
                              helpText={
                                !this.state.isLoading &&
                                this.state.config.pointsForRecommendation ===
                                  '' ? (
                                  <div style={styles.helpTestWrapper}>
                                    {t(
                                      'common.moneyboxconfig.valueCannotBeEmpty'
                                    )}
                                  </div>
                                ) : undefined
                              }
                              inputProps={{
                                disabled: !isGranted(
                                  ROLE_EDIT_MONEY_BOX_CONFIGURATION
                                ),
                                type: 'text',
                                placeholder: t(
                                  'common.moneyboxconfig.typeValue'
                                ),
                                name: 'pointsForRecommendation',
                                value:
                                  this.state.config.pointsForRecommendation,
                                onChange: e =>
                                  this.handleChange(
                                    e.target.name,
                                    e.target.value
                                  ),
                              }}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell>
                          {t('common.moneyboxconfig.pointsQty')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          {t('common.moneyboxconfig.desc8')}
                          <br />
                          <br />
                          {t('common.shared.etc')}{' '}
                          <i>{t('common.moneyboxconfig.etc8')}</i>
                        </TableCell>
                        <TableCell align="right" />
                        <TableCell align="left">
                          <b>{t('common.moneyboxconfig.forAccountCreate')}</b>
                        </TableCell>
                        <TableCell align="right">
                          <b>{t('common.moneyboxconfig.clientGetPoints')}:</b>
                        </TableCell>
                        <TableCell>
                          <div style={styles.customInputWrapper}>
                            <CustomInput
                              formControlProps={{ fullWidth: true }}
                              error={this.state.config.pointsForRegister === ''}
                              helpText={
                                !this.state.isLoading &&
                                this.state.config.pointsForRegister === '' ? (
                                  <div style={styles.helpTestWrapper}>
                                    {t(
                                      'common.moneyboxconfig.valueCannotBeEmpty'
                                    )}
                                  </div>
                                ) : undefined
                              }
                              inputProps={{
                                disabled: !isGranted(
                                  ROLE_EDIT_MONEY_BOX_CONFIGURATION
                                ),
                                type: 'text',
                                placeholder: t(
                                  'common.moneyboxconfig.typeValue'
                                ),
                                name: 'pointsForRegister',
                                value: this.state.config.pointsForRegister,
                                onChange: e =>
                                  this.handleChange(
                                    e.target.name,
                                    e.target.value
                                  ),
                              }}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell>
                          {t('common.moneyboxconfig.pointsQty')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <Trans
                            i18nKey="common.moneyboxconfig.desc10"
                            components={{ b: <b /> }}
                          />
                          <br />
                          <br />
                          {t('common.shared.etc')}{' '}
                          <i>{t('common.moneyboxconfig.etc9')}</i>
                        </TableCell>
                        <TableCell align="right" />
                        <TableCell align="left">
                          <b>{t('common.moneyboxconfig.forAccept')}</b>
                        </TableCell>
                        <TableCell align="right">
                          <b>{t('common.moneyboxconfig.clientGetPoints')}:</b>
                        </TableCell>
                        <TableCell>
                          <div style={styles.customInputWrapper}>
                            <CustomInput
                              formControlProps={{ fullWidth: true }}
                              error={this.state.config.emailBonus === ''}
                              helpText={
                                !this.state.isLoading &&
                                this.state.config.emailBonus === '' ? (
                                  <div style={styles.helpTestWrapper}>
                                    {t(
                                      'common.moneyboxconfig.valueCannotBeEmpty'
                                    )}
                                  </div>
                                ) : undefined
                              }
                              inputProps={{
                                disabled: !isGranted(
                                  ROLE_EDIT_MONEY_BOX_CONFIGURATION
                                ),
                                type: 'text',
                                placeholder: t(
                                  'common.moneyboxconfig.typeValue'
                                ),
                                name: 'emailBonus',
                                value: this.state.config.emailBonus,
                                onChange: e =>
                                  this.handleChange(
                                    e.target.name,
                                    e.target.value
                                  ),
                              }}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell>
                          {t('common.moneyboxconfig.pointsQty')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <Trans
                            i18nKey="common.moneyboxconfig.desc11"
                            components={{ b: <b /> }}
                          />
                          <br />
                          <br />
                          {t('common.shared.etc')}{' '}
                          <i>{t('common.moneyboxconfig.etc9')}</i>
                        </TableCell>
                        <TableCell align="right" />
                        <TableCell align="left">
                          <b>{t('common.moneyboxconfig.forAccept')}</b>
                        </TableCell>
                        <TableCell align="right">
                          <b>{t('common.moneyboxconfig.clientGetPoints')}:</b>
                        </TableCell>
                        <TableCell>
                          <div style={styles.customInputWrapper}>
                            <CustomInput
                              formControlProps={{ fullWidth: true }}
                              error={this.state.config.smsBonus === ''}
                              helpText={
                                !this.state.isLoading &&
                                this.state.config.smsBonus === '' ? (
                                  <div style={styles.helpTestWrapper}>
                                    {t(
                                      'common.moneyboxconfig.valueCannotBeEmpty'
                                    )}
                                  </div>
                                ) : undefined
                              }
                              inputProps={{
                                disabled: !isGranted(
                                  ROLE_EDIT_MONEY_BOX_CONFIGURATION
                                ),
                                type: 'text',
                                placeholder: t(
                                  'common.moneyboxconfig.typeValue'
                                ),
                                name: 'smsBonus',
                                value: this.state.config.smsBonus,
                                onChange: e =>
                                  this.handleChange(
                                    e.target.name,
                                    e.target.value
                                  ),
                              }}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell>
                          {t('common.moneyboxconfig.pointsQty')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <Trans
                            i18nKey="common.moneyboxconfig.desc12"
                            components={{ b: <b /> }}
                          />
                          <br />
                          <br />
                          {t('common.shared.etc')}{' '}
                          <i>{t('common.moneyboxconfig.etc9')}</i>
                        </TableCell>
                        <TableCell align="right" />
                        <TableCell align="left">
                          <b>{t('common.moneyboxconfig.forAccept')}</b>
                        </TableCell>
                        <TableCell align="right">
                          <b>{t('common.moneyboxconfig.clientGetPoints')}:</b>
                        </TableCell>
                        <TableCell>
                          <div style={styles.customInputWrapper}>
                            <CustomInput
                              formControlProps={{ fullWidth: true }}
                              error={this.state.config.phoneBonus === ''}
                              helpText={
                                !this.state.isLoading &&
                                this.state.config.phoneBonus === '' ? (
                                  <div style={styles.helpTestWrapper}>
                                    {t(
                                      'common.moneyboxconfig.valueCannotBeEmpty'
                                    )}
                                  </div>
                                ) : undefined
                              }
                              inputProps={{
                                disabled: !isGranted(
                                  ROLE_EDIT_MONEY_BOX_CONFIGURATION
                                ),
                                type: 'text',
                                placeholder: t(
                                  'common.moneyboxconfig.typeValue'
                                ),
                                name: 'phoneBonus',
                                value: this.state.config.phoneBonus,
                                onChange: e =>
                                  this.handleChange(
                                    e.target.name,
                                    e.target.value
                                  ),
                              }}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </GridItem>
              </GridContainer>
            </GridItem>
          </CardBody>
        </CardWrapper>
        <CardWrapper title={t('common.moneyboxconfig.detailSettings')}>
          <CardBody>
            <GridItem md={12}>
              <GridContainer>
                <GridItem md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="allowPayWithMoneyboxInSubscriptionMode"
                        onClick={e =>
                          this.handleChange(
                            e.target.name,
                            !this.state.config
                              .allowPayWithMoneyboxInSubscriptionMode
                          )
                        }
                        checked={
                          this.state.config
                            .allowPayWithMoneyboxInSubscriptionMode
                        }
                        checkedIcon={<Check className={classes.checkedIcon} />}
                        icon={<Check className={classes.uncheckedIcon} />}
                        classes={{
                          checked: classes.checked,
                          root: classes.checkRoot,
                        }}
                      />
                    }
                    classes={{
                      label: classes.label,
                    }}
                    label={
                      <>
                        {t(
                          'common.moneyboxconfig.detailSettings.allowToPayWithPointsForIntent'
                        )}
                      </>
                    }
                  />
                </GridItem>
              </GridContainer>
            </GridItem>
            <GridItem md={12}>
              <GridContainer>
                <GridItem>
                  <FormTextInput
                    label={t('brandCfg.moneyboxAddr')}
                    classes={classes}
                    name="moneyBoxReadMoreLink"
                    value={this.state.moneyBoxReadMoreLink}
                    maxLength={255}
                    handleChange={e => {
                      this.handleMoneyBoxLinkChange(
                        e.target.value,
                        e.target.name
                      );
                    }}
                    inputSize={12}
                  />
                </GridItem>
              </GridContainer>
            </GridItem>
            <GridItem md={12}>
              <GridContainer>
                <GridItem md={6}>
                  <RadioGroup
                    label={t(
                      'common.moneyboxconfig.detailSettings.validityPeriod'
                    )}
                    name="pointsValidityPeriod"
                    value={this.state.config.pointsValidityPeriod}
                    options={pointsValidityPeriodOptions(t)}
                    disabled={!isGranted(ROLE_EDIT_MONEY_BOX_CONFIGURATION)}
                    onChange={value =>
                      this.setState(prevState => ({
                        config: {
                          ...prevState.config,
                          pointsValidityPeriod: value,
                        },
                      }))
                    }
                  />
                </GridItem>
              </GridContainer>
            </GridItem>
          </CardBody>
        </CardWrapper>
        <Modal
          style={styles.modal}
          aria-labelledby="simple-modal-title"
          aria-describedby="simple-modal-description"
          open={this.state.modalIsOpen}
          onClose={this.handleModal}
        >
          <div style={styles.modalContentWrapper}>
            <Typography style={styles.typography} variant="h5" id="modal-title">
              {t('$*common.confirmationModal.title')}
            </Typography>
            <Typography variant="subtitle1" id="simple-modal-description">
              {t('moneyBoxConfiguration.confirmationModal.description')}
            </Typography>
            <div style={styles.modalButtonWrapper}>
              <Button
                style={styles.buttonYes}
                variant="contained"
                color="primary"
                className={classes.button}
                onClick={this.handleModal}
              >
                {t('common.no')}
              </Button>
              <Button
                style={styles.buttonNo}
                variant="contained"
                color="primary"
                className={classes.button}
                onClick={this.handleSubmit}
              >
                {t('common.yes')}
              </Button>
            </div>
          </div>
        </Modal>
        {isGranted(ROLE_EDIT_MONEY_BOX_CONFIGURATION) && (
          <LogView
            classes={classes}
            handleSubmit={() => this.handleModal()}
            submitDisabled={this.state.isLoading}
            submitText={t('common.shared.save')}
            iri="/money-box-configurations"
          />
        )}
        {!isGranted(ROLE_EDIT_MONEY_BOX_CONFIGURATION) && (
          <LogView iri="/money-box-configurations" />
        )}
      </form>
    );
  }
}

const combinedStyles = combineStyles(
  extendedFormsStyle,
  validationFormsStyle,
  buttonsStyle
);

const mapStateToProps = ({ Brands: { brand } }) => ({
  multinational: brand.multinational,
  brandId: brand.id,
  moneyBoxReadMoreLink: brand.moneyBoxReadMoreLink,
});

const enhance = compose(
  connect(mapStateToProps),
  withStyles(combinedStyles),
  withTranslation()
);

export default enhance(MoneyBoxConfigurations);
